import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Calculator,
  LogOut,
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  UserCheck,
  Euro,
  Percent,
  Mail,
  User
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (v) =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

const AdminFixedPermFees = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [fees, setFees] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    tiers: [{ min_salary: 0, max_salary: null, fee_percentage: 15, description: "" }],
    active: true
  });

  useEffect(() => { loadFees(); }, []);

  const loadFees = async () => {
    try {
      const res = await axios.get(`${API}/fixed-perm-fees`, { headers: getAuthHeaders() });
      setFees(res.data);
    } catch { toast.error("Erro ao carregar fee tiers fixos"); }
    finally { setLoading(false); }
  };

  const handleLogout = () => { logout(); navigate("/"); };

  const openNewDialog = () => {
    setEditingFee(null);
    setFormData({
      email: "", name: "",
      tiers: [{ min_salary: 0, max_salary: null, fee_percentage: 15, description: "" }],
      active: true
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (fee) => {
    setEditingFee(fee);
    setFormData({
      email: fee.email,
      name: fee.name || "",
      tiers: fee.tiers || [{ min_salary: 0, max_salary: null, fee_percentage: 15, description: "" }],
      active: fee.active !== false
    });
    setIsDialogOpen(true);
  };

  const addTier = () => {
    const last = formData.tiers[formData.tiers.length - 1];
    const newMin = last?.max_salary || (last?.min_salary + 10000) || 0;
    setFormData({
      ...formData,
      tiers: [...formData.tiers, { min_salary: newMin, max_salary: null, fee_percentage: 15, description: "" }]
    });
  };

  const removeTier = (index) => {
    if (formData.tiers.length <= 1) { toast.error("Deve existir pelo menos um nível"); return; }
    setFormData({ ...formData, tiers: formData.tiers.filter((_, i) => i !== index) });
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...formData.tiers];
    if (field === "description") {
      newTiers[index][field] = value;
    } else if ((field === "max_salary") && value === "") {
      newTiers[index][field] = null;
    } else {
      newTiers[index][field] = parseFloat(value) || 0;
    }
    setFormData({ ...formData, tiers: newTiers });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email.trim()) { toast.error("Email é obrigatório"); return; }
    try {
      if (editingFee) {
        await axios.put(`${API}/fixed-perm-fees/${editingFee.id}`, formData, { headers: getAuthHeaders() });
        toast.success("Fee Perm fixo atualizado!");
      } else {
        await axios.post(`${API}/fixed-perm-fees`, formData, { headers: getAuthHeaders() });
        toast.success("Fee Perm fixo criado!");
      }
      setIsDialogOpen(false);
      loadFees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar");
    }
  };

  const handleDelete = async (feeId) => {
    if (!window.confirm("Eliminar este fee perm fixo?")) return;
    try {
      await axios.delete(`${API}/fixed-perm-fees/${feeId}`, { headers: getAuthHeaders() });
      toast.success("Fee Perm fixo eliminado");
      loadFees();
    } catch { toast.error("Erro ao eliminar"); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-fixed-perm-fees-page">
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-600 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-manrope font-bold text-lg text-foreground">Evolutt</span>
              <p className="text-xs text-muted-foreground">Fee Perm Fixo</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="outline" size="sm" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
              </Button>
            </Link>
            <div>
              <h1 className="font-manrope font-extrabold text-2xl text-foreground flex items-center gap-2">
                <UserCheck className="w-6 h-6 text-indigo-500" />
                Fee Perm Fixo por Utilizador
              </h1>
              <p className="text-muted-foreground">
                Atribuir escalões de fee Perm específicos a utilizadores por email
              </p>
            </div>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-600 text-white hover:bg-brand-700 font-bold" onClick={openNewDialog} data-testid="add-fixed-perm-fee-btn">
                <Plus className="w-4 h-4 mr-2" /> Novo Fee Fixo
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-manrope font-bold text-xl">
                  {editingFee ? "Editar Fee Perm Fixo" : "Novo Fee Perm Fixo"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="utilizador@empresa.pt" required data-testid="fee-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> Nome</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Nome do colaborador" data-testid="fee-name-input" />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="font-semibold">Escalões (por Salário Anual)</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addTier} data-testid="add-fee-tier-btn">
                      <Plus className="w-4 h-4 mr-1" /> Nível
                    </Button>
                  </div>

                  <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200 mb-2">
                    <p className="text-xs text-indigo-800">
                      Define a percentagem de fee a cobrar ao cliente com base no salário anual do candidato. Este fee é aplicado automaticamente quando o utilizador faz uma simulação Perm.
                    </p>
                  </div>

                  <div className="space-y-3">
                    {formData.tiers.map((tier, index) => (
                      <div key={index} className="p-4 rounded-lg border-2 bg-slate-50 border-slate-200">
                        <div className="grid grid-cols-5 gap-3 items-end">
                          <div>
                            <Label className="text-xs text-muted-foreground">Salário Mín.</Label>
                            <div className="relative">
                              <Input type="number" min="0" step="1000" className="font-mono pl-8" value={tier.min_salary} onChange={(e) => updateTier(index, "min_salary", e.target.value)} />
                              <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Salário Máx.</Label>
                            <div className="relative">
                              <Input type="number" min="0" step="1000" className="font-mono pl-8" value={tier.max_salary === null ? "" : tier.max_salary} onChange={(e) => updateTier(index, "max_salary", e.target.value)} placeholder="sem limite" />
                              <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Fee %</Label>
                            <div className="relative">
                              <Input type="number" min="0" max="100" step="0.5" className="font-mono pr-8 font-bold" value={tier.fee_percentage} onChange={(e) => updateTier(index, "fee_percentage", e.target.value)} />
                              <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Descrição</Label>
                            <Input value={tier.description} onChange={(e) => updateTier(index, "description", e.target.value)} placeholder="Ex: Até 25k" />
                          </div>
                          <Button type="button" variant="ghost" size="icon" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => removeTier(index)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground">
                          Salário {formatCurrency(tier.min_salary)}-{tier.max_salary ? formatCurrency(tier.max_salary) : "sem limite"}: Fee {tier.fee_percentage}%
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-primary text-primary-foreground" data-testid="save-fixed-perm-fee-btn">
                    {editingFee ? "Guardar Alterações" : "Criar Fee"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="bg-white border border-border/60 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : fees.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <UserCheck className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Nenhum fee perm fixo configurado</p>
                <p className="text-sm text-muted-foreground">Atribua escalões de fee Perm específicos a utilizadores</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">Email</TableHead>
                    <TableHead className="font-semibold">Nome</TableHead>
                    <TableHead className="font-semibold">Escalões</TableHead>
                    <TableHead className="font-semibold">Estado</TableHead>
                    <TableHead className="font-semibold text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fees.map((fee) => (
                    <TableRow key={fee.id} data-testid={`perm-fee-row-${fee.id}`}>
                      <TableCell className="font-mono text-sm">{fee.email}</TableCell>
                      <TableCell>{fee.name || "—"}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(fee.tiers || []).map((t, i) => (
                            <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {formatCurrency(t.min_salary)}-{t.max_salary ? formatCurrency(t.max_salary) : "max"}: {t.fee_percentage}%
                            </span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${fee.active !== false ? "bg-brand-100 text-brand-800" : "bg-gray-100 text-gray-600"}`}>
                          {fee.active !== false ? "Ativo" : "Inativo"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditDialog(fee)} data-testid={`edit-perm-fee-${fee.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(fee.id)} data-testid={`delete-perm-fee-${fee.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminFixedPermFees;
